import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import ScrollAnimation from '../components/ScrollAnimation';
import { ArrowRight, ArrowUpRight } from 'lucide-react';

const HYPERDREAM_URL = 'https://hyperdream.co';

const workflow = [
  {
    n: '01',
    title: 'AI Scriptwriting',
    description: 'Turn a prompt or logline into a structured screenplay with acts, scenes, and dialogue you can edit.'
  },
  {
    n: '02',
    title: 'Style & Look',
    description: 'Lock a consistent cinematic look — lighting, colour grade, and film grain — that carries across every shot.'
  },
  {
    n: '03',
    title: 'Characters & Casting',
    description: 'Create consistent AI actors and clone voices, so the same character looks and sounds right in every scene.'
  },
  {
    n: '04',
    title: 'Storyboarding',
    description: 'Generate a shot-by-shot storyboard from your script with consistent characters and framing.'
  },
  {
    n: '05',
    title: 'Video Generation',
    description: 'Generate video with virtual camera control, shot blocking, and lens choices — directed, not random.'
  },
  {
    n: '06',
    title: 'Editing & Export',
    description: 'Assemble shots, add sound, and export a finished film in a built-in non-destructive editor.'
  }
];

const Hyperdream = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 text-gray-900">
      {/* Hero */}
      <section className="pt-28 pb-20 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <ScrollAnimation>
            <div className="flex justify-center mb-10">
              <img
                src="/assets/images/hyperdream_logo.png"
                alt="Hyperdream Logo"
                className="h-24 md:h-32 object-contain"
              />
            </div>
          </ScrollAnimation>

          <ScrollAnimation delay={150}>
            <h1 className="text-4xl md:text-6xl font-league-spartan font-extralight text-gray-900 mb-6 tracking-tight leading-tight">
              From script to screen,<br />in one place.
            </h1>
          </ScrollAnimation>

          <ScrollAnimation delay={250}>
            <p className="text-xl text-gray-600 font-league-spartan font-light leading-relaxed max-w-2xl mx-auto mb-10">
              Hyperdream is an end-to-end AI film studio. Write the script, design the
              look, cast AI characters, storyboard, generate video, and edit — all in one
              platform that keeps continuous context, so your characters and style stay
              consistent across every scene.
            </p>
          </ScrollAnimation>

          <ScrollAnimation delay={350}>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
                <a href={HYPERDREAM_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                  Visit Hyperdream
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/contact" className="flex items-center gap-2">
                  Talk to us
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </ScrollAnimation>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 px-6 lg:px-8 border-t border-gray-100">
        <div className="max-w-2xl mx-auto">
          <ScrollAnimation>
            <p className="text-gray-600 font-league-spartan leading-relaxed mb-6">
              We’ve helped many people build their AI solutions. A while back we decided
              to give it a go for ourselves — and we found a gap.
            </p>
            <p className="text-gray-600 font-league-spartan leading-relaxed mb-6">
              People wanted to create AI videos that stayed consistent: the same
              characters, the same tone, the same story. Most tools treated every prompt
              as a separate task, and the context vanished each time you switched. So we
              filled the gap.
            </p>
            <p className="text-gray-600 font-league-spartan leading-relaxed">
              Hyperdream is the process we came up with — script, style, casting,
              storyboard, generation, and edit — in one flow. It’s live now.
            </p>
          </ScrollAnimation>
        </div>
      </section>

      {/* Workflow */}
      <section className="py-24 px-6 lg:px-8 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <ScrollAnimation className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-league-spartan font-light text-gray-900 mb-4 tracking-tight">
              One connected pipeline
            </h2>
            <p className="text-gray-600 font-league-spartan font-light max-w-xl mx-auto leading-relaxed">
              Six stages that share the same characters, style, and story context.
            </p>
          </ScrollAnimation>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10">
            {workflow.map((step, index) => (
              <ScrollAnimation key={step.n} delay={index * 100}>
                <div className="border-t border-gray-200 pt-5">
                  <div className="text-sm font-league-spartan text-primary/50 mb-2">
                    {step.n}
                  </div>
                  <h3 className="font-league-spartan font-medium text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 font-league-spartan text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-24 px-6 lg:px-8 border-t border-gray-100">
        <div className="max-w-2xl mx-auto text-center">
          <ScrollAnimation>
            <h2 className="text-3xl md:text-4xl font-league-spartan font-light text-gray-900 mb-6 tracking-tight">
              Start your film
            </h2>
            <p className="text-gray-600 font-league-spartan font-light leading-relaxed mb-10">
              Hyperdream is open to everyone. Try it, or talk to us about building
              something similar for your own product.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
                <a href={HYPERDREAM_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                  Go to hyperdream.co
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </Button>
              <Button asChild size="lg" variant="ghost">
                <Link to="/contact" className="flex items-center gap-2 text-gray-600 hover:text-primary">
                  Talk to us
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </ScrollAnimation>
        </div>
      </section>
    </div>
  );
};

export default Hyperdream;
